import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  KeyRound, Webhook, Plus, Trash2, Copy, Send, RefreshCw,
  CheckCircle2, XCircle, Globe, ExternalLink, ArrowDownToLine, Eye, EyeOff, RotateCw,
  Download, Layers, Building2, Users2, Briefcase, ScaleIcon, AlertTriangle, Mail
} from "lucide-react";

// =============================================================================
// /integrations admin page — API Keys + Webhooks
// =============================================================================
import SenderManagerModal from "@/components/invoices/SenderManagerModal";

export default function Integrations() {
  return (
    <div className="space-y-6 animate-fade-up" data-testid="integrations-page">
      <div>
        <div className="label-mono">PLATFORM API</div>
        <h1 className="font-heading text-4xl font-black tracking-tight leading-none mt-1">Integrations</h1>
        <p className="text-sm text-zinc-600 mt-1 font-mono">
          Let external systems (e.g. surveydrafts.us) read from Land Drafters and subscribe to events.
        </p>
      </div>

      <ApiBaseBanner />
      <EmailConfigPanel />
      <BulkImportPanel />
      <ReconciliationPanel />
      <ApiKeysPanel />
      <WebhooksPanel />
      <InboundFromSurveydraftsPanel />
      <Docs />
    </div>
  );
}

function EmailConfigPanel() {
  const [senders, setSenders] = useState([]);
  const [showManager, setShowManager] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get("/invoice-senders");
      setSenders(data || []);
    } catch { setSenders([]); }
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="brut-card p-4" data-testid="email-config-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-mono">EMAIL / SMTP</div>
          <div className="font-heading font-black text-lg">Outgoing Email Account</div>
          <p className="text-xs font-mono text-zinc-600 mt-1 max-w-lg">
            Used for EVERYTHING the app sends: welcome/credential emails, document delivery,
            signing OTP codes, notifications digest and invoices. Add your Gmail with an App Password.
          </p>
          <div className="mt-2 space-y-1">
            {senders.length === 0 && (
              <div className="text-xs font-mono text-rose-600" data-testid="email-config-none">
                No sender configured — emails will NOT be sent until you add one.
              </div>
            )}
            {senders.map((s) => (
              <div key={s.id} className="text-xs font-mono text-emerald-700" data-testid={`email-config-sender-${s.id}`}>
                ✓ {s.display_name ? `${s.display_name} — ` : ""}{s.email} ({s.smtp_host}:{s.smtp_port})
              </div>
            ))}
          </div>
        </div>
        <button
          onClick={() => setShowManager(true)}
          className="brut-btn px-3 py-2 text-xs bg-emerald-300 hover:bg-emerald-400"
          data-testid="email-config-manage"
        >
          MANAGE EMAIL ACCOUNTS
        </button>
      </div>
      {showManager && (
        <SenderManagerModal senders={senders} onClose={() => setShowManager(false)} onChanged={load} />
      )}
    </div>
  );
}

function ApiBaseBanner() {
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  return (
    <div className="brut-card p-4 bg-yellow-50">
      <div className="label-mono text-zinc-500">YOUR API BASE URL</div>
      <div className="flex items-center gap-2 mt-1">
        <code className="font-mono text-sm font-bold flex-1 truncate" title={apiBase}>{apiBase}/api</code>
        <button
          onClick={() => { navigator.clipboard.writeText(`${apiBase}/api`); toast.success("Copied"); }}
          className="brut-btn-secondary px-2 py-1 text-[10px] inline-flex items-center gap-1"
          data-testid="copy-api-base"
        ><Copy className="w-3 h-3" /> COPY</button>
      </div>
      <div className="text-[10px] font-mono text-zinc-500 mt-1">
        All external endpoints listed below are relative to this base.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ONE-TIME bulk import from Surveydrafts.us
// Lets the admin pull orgs / clients / teams / live jobs in one click.
function BulkImportPanel() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [last, setLast] = useState(null);

  // Selections — default to all on, jobs live-only
  const [pick, setPick] = useState({ orgs: true, clients: true, teams: true, jobs: true, live_only: true });
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);  // shown while running
  const [result, setResult] = useState(null);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sd-bulk-import/preview");
      setPreview(data);
    } catch (e) {
      setPreview({ ok: false, error: e.response?.data?.detail || "Failed" });
    }
    try {
      const { data } = await api.get("/sd-bulk-import/last-run");
      setLast(data?.last_run || null);
    } catch { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { loadPreview(); }, []);

  const runImport = async () => {
    if (!confirm(`Run one-time bulk import?\n\nThis will pull from Surveydrafts.us and UPSERT into Land Drafters:\n${pick.orgs ? "✓ orgs\n" : ""}${pick.clients ? "✓ clients\n" : ""}${pick.teams ? "✓ teams + members\n" : ""}${pick.jobs ? `✓ jobs (${pick.live_only ? "live only" : "all"})\n` : ""}\nIt's idempotent and safe to re-run.`)) return;
    setRunning(true);
    setResult(null);
    setProgress("Pulling from Surveydrafts.us…");
    try {
      const { data } = await api.post("/sd-bulk-import/run", pick);
      setResult(data);
      setLast(data);
      toast.success("Bulk import finished");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
      setResult({ ok: false, error: e.response?.data?.detail || "Failed" });
    }
    setRunning(false);
    setProgress(null);
  };

  const counts = preview?.counts || {};
  const rows = [
    { key: "orgs",    icon: <Building2 className="w-4 h-4" />, label: "Orgs",           remote: counts.orgs,             help: "SD survey organizations (Civil 3D Drafters, Land Drafters LLC, …) → cached in sd_organizations" },
    { key: "clients", icon: <Briefcase className="w-4 h-4" />, label: "Clients",        remote: counts.clients,          help: "Full client records (incl. billing_address, currency, rate_per_hour) → clients collection" },
    { key: "teams",   icon: <Users2 className="w-4 h-4" />,    label: "Teams + Members", remote: `${counts.teams_vendors ?? "?"} / ${counts.team_members ?? "?"}`, help: "SD vendors (renamed Teams) + their members → vendors + sd_team_members" },
    { key: "jobs",    icon: <Layers className="w-4 h-4" />,    label: "Jobs",           remote: counts.jobs_live,        help: `Live jobs only (filter respected if SD has practice/training). ${counts.jobs_live ?? "?"} live of ${counts.jobs_all ?? "?"} total`, hasLiveToggle: true },
  ];

  return (
    <div className="brut-card p-5 bg-amber-50/40">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Download className="w-4 h-4" />
          <h2 className="font-heading text-xl font-black">One-Time Bulk Import from Surveydrafts.us</h2>
        </div>
        <button
          onClick={loadPreview}
          disabled={loading}
          className="brut-btn-secondary px-2 py-1 text-xs inline-flex items-center gap-1"
          data-testid="bulk-import-refresh-preview"
        ><RefreshCw className="w-3 h-3" /> {loading ? "…" : "REFRESH"}</button>
      </div>

      <p className="text-xs font-mono text-zinc-700 mb-4">
        Pulls a full snapshot from SD and UPSERTs into LD. Idempotent — re-running just updates fields. Use this once after connecting (or whenever you want to backfill).
      </p>

      {/* Selection list */}
      <div className="space-y-1.5 mb-3" data-testid="bulk-import-selection">
        {rows.map((r) => (
          <label
            key={r.key}
            className={`brut-card bg-white p-3 flex items-start gap-3 cursor-pointer ${pick[r.key] ? "" : "opacity-60"}`}
            data-testid={`bulk-import-row-${r.key}`}
          >
            <input
              type="checkbox"
              checked={pick[r.key]}
              onChange={(e) => setPick({ ...pick, [r.key]: e.target.checked })}
              className="mt-1"
              data-testid={`bulk-import-toggle-${r.key}`}
            />
            <span className="mt-0.5">{r.icon}</span>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm">{r.label}
                <span className="ml-2 label-mono text-zinc-500">
                  {r.remote === undefined || r.remote === null ? "—" : r.remote === -1 ? "n/a" : `${r.remote} on SD`}
                </span>
              </div>
              <div className="text-[10px] font-mono text-zinc-600">{r.help}</div>
              {r.hasLiveToggle && (
                <label className="text-[10px] font-mono mt-1 inline-flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={pick.live_only}
                    onChange={(e) => setPick({ ...pick, live_only: e.target.checked })}
                    data-testid="bulk-import-live-only"
                  />
                  LIVE ONLY (exclude practice/training)
                </label>
              )}
            </div>
          </label>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono text-zinc-500">
          {last?.finished_at && <>Last run: {new Date(last.finished_at).toLocaleString()}</>}
        </div>
        <button
          onClick={runImport}
          disabled={running || (!pick.orgs && !pick.clients && !pick.teams && !pick.jobs)}
          className="brut-btn px-4 py-2 text-sm bg-emerald-300 hover:bg-emerald-400 disabled:opacity-50 inline-flex items-center gap-2"
          data-testid="bulk-import-run"
        >
          <Download className="w-4 h-4" />
          {running ? "IMPORTING…" : "RUN BULK IMPORT"}
        </button>
      </div>

      {running && (
        <div className="mt-3 brut-card bg-white p-3 text-xs font-mono">
          <div className="flex items-center gap-2">
            <RefreshCw className="w-3 h-3 animate-spin" />
            <span>{progress || "Working…"}</span>
          </div>
          <div className="text-[10px] text-zinc-500 mt-1">Jobs ~3,900 records → typically 15–30 seconds. Do not close the tab.</div>
        </div>
      )}

      {result && <BulkImportResult data={result} />}
      {!result && last && <BulkImportResult data={last} stale />}
    </div>
  );
}

function BulkImportResult({ data, stale }) {
  if (!data?.ok && data?.error) {
    return (
      <div className="mt-3 brut-card bg-rose-50 border-rose-700 p-3 text-xs font-mono text-rose-800" data-testid="bulk-import-error">
        <div className="label-mono">IMPORT FAILED</div>
        <div className="mt-1">{data.error}</div>
      </div>
    );
  }
  const items = [];
  if (data.orgs)    items.push(["Orgs",         `${data.orgs.created}↑ / ${data.orgs.updated}~ / ${data.orgs.rows} rows`]);
  if (data.clients) items.push(["Clients",      `${data.clients.created}↑ / ${data.clients.updated}~ / ${data.clients.rows} rows`]);
  if (data.teams)   items.push(["Teams",        `${data.teams.vendors.created + data.teams.team_members.created}↑ / ${data.teams.vendors.updated + data.teams.team_members.updated}~ (${data.teams.vendors.rows} vendors + ${data.teams.team_members.rows} members)`]);
  if (data.jobs)    items.push([`Jobs (${data.jobs.filter})`, `${data.jobs.created}↑ / ${data.jobs.updated}~ / ${data.jobs.rows} rows ${data.jobs.skipped ? `(${data.jobs.skipped} skipped)` : ""}`]);
  return (
    <div className={`mt-3 brut-card p-3 text-xs font-mono ${stale ? "bg-zinc-50" : "bg-emerald-50 border-emerald-700"}`} data-testid="bulk-import-result">
      <div className="label-mono">{stale ? "LAST RUN RESULTS" : "✓ IMPORT COMPLETE"}</div>
      <table className="w-full mt-1.5">
        <tbody>
          {items.map(([k, v]) => (
            <tr key={k} className="border-b last:border-0 border-zinc-200">
              <td className="py-1 pr-3 text-zinc-600">{k}</td>
              <td className="py-1 font-bold">{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-[10px] text-zinc-500 mt-1">↑ created · ~ updated</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LD ↔ Surveydrafts reconciliation — nightly drift check + email alerts.
function ReconciliationPanel() {
  const [latest, setLatest] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [history, setHistory] = useState(null);
  const [running, setRunning] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const [threshold, setThreshold] = useState("1");
  const [recipients, setRecipients] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [runHour, setRunHour] = useState("0");
  const [runMin, setRunMin] = useState("30");

  const loadAll = async () => {
    try {
      const [l, c] = await Promise.all([
        api.get("/sd-reconciliation/latest"),
        api.get("/sd-reconciliation/config"),
      ]);
      setLatest(l.data);
      setCfg(c.data);
      setThreshold(String(c.data.drift_threshold_pct ?? "1"));
      setRecipients((c.data.recipients || []).join(", "));
      setEnabled(!!c.data.enabled);
      setRunHour(String(c.data.run_hour_utc ?? "0"));
      setRunMin(String(c.data.run_minute_utc ?? "30"));
    } catch { /* ignore */ }
  };
  useEffect(() => { loadAll(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/sd-reconciliation/run");
      setLatest(data);
      if (data.any_alert) toast.warning("Drift detected — see table");
      else toast.success("All in sync");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reconciliation failed");
    }
    setRunning(false);
  };

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      const body = {
        enabled,
        drift_threshold_pct: parseFloat(threshold) || 1,
        recipients: recipients.split(",").map((s) => s.trim()).filter(Boolean),
        run_hour_utc: parseInt(runHour, 10) || 0,
        run_minute_utc: parseInt(runMin, 10) || 0,
      };
      const { data } = await api.put("/sd-reconciliation/config", body);
      setCfg(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSavingCfg(false);
  };

  const openHistory = async () => {
    setShowHistory(true);
    setHistory(null);
    try {
      const { data } = await api.get("/sd-reconciliation/history", { params: { limit: 30 } });
      setHistory(data || []);
    } catch { setHistory([]); }
  };

  const drift = latest?.drift || [];
  const hasRun = latest && !latest.empty;

  return (
    <div className="brut-card p-5 bg-sky-50/40">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ScaleIcon className="w-4 h-4" />
          <h2 className="font-heading text-xl font-black">Reconciliation (LD ↔ Surveydrafts.us)</h2>
          {hasRun && latest?.any_alert && (
            <span className="label-mono text-rose-700 border border-rose-700 px-1.5 py-0.5 inline-flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> DRIFT
            </span>
          )}
          {hasRun && !latest?.any_alert && !latest?.error && (
            <span className="label-mono text-emerald-700 border border-emerald-700 px-1.5 py-0.5">IN SYNC</span>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={openHistory} className="brut-btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1" data-testid="recon-history">
            HISTORY
          </button>
          <button onClick={runNow} disabled={running} className="brut-btn px-3 py-1.5 text-xs bg-emerald-300 hover:bg-emerald-400 inline-flex items-center gap-1 disabled:opacity-50" data-testid="recon-run-now">
            <RefreshCw className={`w-3 h-3 ${running ? "animate-spin" : ""}`} /> {running ? "CHECKING…" : "RUN NOW"}
          </button>
        </div>
      </div>

      <p className="text-xs font-mono text-zinc-700 mb-3">
        Nightly job compares LD counts against SD totals. If drift exceeds the threshold, alerts email the recipients below.
      </p>

      {/* Drift table */}
      {latest?.error && (
        <div className="brut-card bg-rose-50 border-rose-700 p-3 text-xs font-mono text-rose-800 mb-3">
          ⚠ {latest.error}
        </div>
      )}
      {hasRun && !latest.error && (
        <div className="brut-card bg-white overflow-hidden" data-testid="recon-drift-table">
          <table className="w-full text-xs font-mono">
            <thead className="bg-zinc-50 border-b border-zinc-300">
              <tr>
                <th className="text-left p-2">ENTITY</th>
                <th className="text-right p-2">ON SD</th>
                <th className="text-right p-2">ON LD</th>
                <th className="text-right p-2">DIFF</th>
                <th className="text-right p-2">DRIFT %</th>
                <th className="text-center p-2">STATUS</th>
              </tr>
            </thead>
            <tbody>
              {drift.map((r) => (
                <tr key={r.entity} className={`border-b border-zinc-100 ${r.alert ? "bg-rose-50" : ""}`} data-testid={`recon-row-${r.entity}`}>
                  <td className="p-2 capitalize">{r.entity.replace(/_/g, " ")}</td>
                  <td className="p-2 text-right">{r.skipped ? "—" : r.remote}</td>
                  <td className="p-2 text-right">{r.local}</td>
                  <td className={`p-2 text-right font-bold ${(r.diff || 0) > 0 ? "text-amber-700" : (r.diff || 0) < 0 ? "text-rose-700" : "text-zinc-500"}`}>
                    {r.skipped ? "—" : (r.diff > 0 ? "+" : "") + r.diff}
                  </td>
                  <td className="p-2 text-right">{r.skipped ? "—" : `${r.drift_pct}%`}</td>
                  <td className="p-2 text-center">
                    {r.skipped ? <span className="text-zinc-400">SKIP</span>
                      : r.alert ? <XCircle className="w-4 h-4 text-rose-700 inline" />
                      : <CheckCircle2 className="w-4 h-4 text-emerald-700 inline" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-2 py-1.5 bg-zinc-50 border-t border-zinc-200 flex items-center justify-between text-[10px] font-mono text-zinc-600">
            <span>Ran {new Date(latest.ran_at).toLocaleString()} · {latest.triggered_by || "manual"}</span>
            {latest.any_alert && (
              latest.emailed_to?.length > 0
                ? <span className="text-emerald-700"><Mail className="w-3 h-3 inline mr-1" /> alerted {latest.emailed_to.length} recipient(s)</span>
                : <span className="text-amber-700">alert pending — no recipients or SMTP not configured</span>
            )}
          </div>
        </div>
      )}
      {!hasRun && (
        <div className="brut-card bg-white p-4 text-xs font-mono text-zinc-500 text-center">
          No runs yet. Click RUN NOW or wait for the nightly schedule.
        </div>
      )}

      {/* Config */}
      <div className="brut-card bg-white p-3 mt-3">
        <div className="label-mono text-zinc-500 mb-2">SETTINGS</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="label-mono text-zinc-500 block">DRIFT THRESHOLD (%)</label>
            <input
              type="number" step="0.1" min="0" max="100"
              value={threshold} onChange={(e) => setThreshold(e.target.value)}
              className="brut-input w-full text-sm mt-0.5"
              data-testid="recon-threshold"
            />
          </div>
          <div>
            <label className="label-mono text-zinc-500 block">DAILY RUN (UTC)</label>
            <div className="flex gap-1 mt-0.5">
              <input type="number" min="0" max="23" value={runHour} onChange={(e) => setRunHour(e.target.value)}
                className="brut-input w-full text-sm" data-testid="recon-hour" />
              <span className="self-center font-mono">:</span>
              <input type="number" min="0" max="59" value={runMin} onChange={(e) => setRunMin(e.target.value)}
                className="brut-input w-full text-sm" data-testid="recon-min" />
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="label-mono text-zinc-500 block">ALERT RECIPIENTS (comma-separated emails — leave empty to alert all admins)</label>
            <input
              value={recipients} onChange={(e) => setRecipients(e.target.value)}
              placeholder="ops@landdrafters.in, cto@landdrafters.in"
              className="brut-input w-full text-sm mt-0.5"
              data-testid="recon-recipients"
            />
          </div>
        </div>
        <div className="flex items-center justify-between mt-3">
          <label className="text-xs font-mono inline-flex items-center gap-1.5" data-testid="recon-enabled-label">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="recon-enabled" />
            SCHEDULE ENABLED
          </label>
          <button onClick={saveCfg} disabled={savingCfg} className="brut-btn px-3 py-1.5 text-xs disabled:opacity-50" data-testid="recon-save-cfg">
            {savingCfg ? "SAVING…" : "SAVE SETTINGS"}
          </button>
        </div>
      </div>

      {showHistory && <ReconHistoryModal rows={history} onClose={() => setShowHistory(false)} />}
    </div>
  );
}

function ReconHistoryModal({ rows, onClose }) {
  return (
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-3" onClick={onClose}>
      <div className="brut-card max-w-3xl w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b-2 border-zinc-900 bg-white">
          <div className="label-mono">RECONCILIATION HISTORY</div>
          <h3 className="font-heading text-lg font-black">Last 30 runs</h3>
        </div>
        <div className="flex-1 overflow-y-auto">
          {rows === null && <div className="p-6 text-xs font-mono text-zinc-500">Loading…</div>}
          {rows?.length === 0 && <div className="p-6 text-xs font-mono text-zinc-500 text-center">No history yet.</div>}
          {rows?.length > 0 && (
            <table className="w-full text-xs font-mono">
              <thead className="bg-zinc-50 border-b border-zinc-300 sticky top-0">
                <tr>
                  <th className="text-left p-2">RAN AT</th>
                  <th className="text-left p-2">TRIGGER</th>
                  <th className="text-center p-2">RESULT</th>
                  <th className="text-left p-2">ALERTS</th>
                  <th className="text-center p-2">EMAILED</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const alerts = (r.drift || []).filter((x) => x.alert).map((x) => x.entity);
                  return (
                    <tr key={r.id} className="border-b border-zinc-100">
                      <td className="p-2 whitespace-nowrap">{new Date(r.ran_at).toLocaleString()}</td>
                      <td className="p-2 text-zinc-500">{r.triggered_by || "—"}</td>
                      <td className="p-2 text-center">
                        {r.error ? <XCircle className="w-3.5 h-3.5 text-rose-700 inline" />
                          : r.any_alert ? <AlertTriangle className="w-3.5 h-3.5 text-amber-700 inline" />
                          : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700 inline" />}
                      </td>
                      <td className="p-2">{alerts.length ? alerts.join(", ") : "—"}</td>
                      <td className="p-2 text-center">{r.emailed_to?.length ? `${r.emailed_to.length} ✓` : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className="p-3 border-t border-zinc-300 bg-zinc-50 text-right">
          <button onClick={onClose} className="brut-btn-secondary px-3 py-1.5 text-xs">CLOSE</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function ApiKeysPanel() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [justMinted, setJustMinted] = useState(null);

  const load = async () => {
    let data = [];
    try {
      const resp = await api.get("/integrations/api-keys");
      data = resp.data || [];
    } catch { /* keep [] */ }
    setKeys(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const [creating, setCreating] = useState(false);
  const onCreate = async (label, scope) => {
    setCreating(true);
    try {
      const resp = await api.post("/integrations/api-keys", { label, scope });
      setJustMinted(resp.data);
      setShowNew(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setCreating(false);
  };

  const onRevoke = async (id) => {
    if (!confirm("Revoke this key? External calls using it will start returning 401.")) return;
    try {
      await api.delete(`/integrations/api-keys/${id}`);
      toast.success("Revoked");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="brut-card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4" />
          <h2 className="font-heading text-xl font-black">API Keys</h2>
          <span className="label-mono text-zinc-500">{keys.length}</span>
        </div>
        <button onClick={() => setShowNew(true)} className="brut-btn px-3 py-1.5 text-xs bg-emerald-300 hover:bg-emerald-400 inline-flex items-center gap-1" data-testid="create-key-btn">
          <Plus className="w-3 h-3" /> NEW KEY
        </button>
      </div>

      {justMinted && (
        <div className="border-2 border-emerald-700 bg-emerald-50 p-3 mb-4" data-testid="just-minted-key">
          <div className="label-mono text-emerald-800">✓ KEY CREATED — COPY IT NOW (cannot be retrieved later)</div>
          <div className="flex gap-2 mt-2 items-center">
            <code className="font-mono text-sm font-bold flex-1 truncate select-all">{justMinted.raw_key}</code>
            <button onClick={() => { navigator.clipboard.writeText(justMinted.raw_key); toast.success("Copied"); }} className="brut-btn px-2 py-1 text-xs">COPY</button>
            <button onClick={() => setJustMinted(null)} className="brut-btn-secondary px-2 py-1 text-xs">DISMISS</button>
          </div>
        </div>
      )}

      {loading && <div className="text-xs font-mono text-zinc-500">Loading…</div>}
      {!loading && keys.length === 0 && (
        <div className="text-xs font-mono text-zinc-500 py-6 text-center">No keys yet. Click NEW KEY to mint one.</div>
      )}
      {keys.length > 0 && (
        <table className="w-full text-xs font-mono" data-testid="api-keys-table">
          <thead className="border-b-2 border-zinc-900 bg-zinc-50">
            <tr><th className="text-left p-2">LABEL</th><th className="text-left p-2">PREFIX</th><th className="text-left p-2">SCOPE</th><th className="text-right p-2">CALLS</th><th className="text-left p-2">LAST USED</th><th className="text-left p-2">STATUS</th><th></th></tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id} className="border-b border-zinc-100" data-testid={`key-row-${k.id}`}>
                <td className="p-2">{k.label}</td>
                <td className="p-2"><code className="bg-zinc-100 px-1">{k.key_prefix}…</code></td>
                <td className="p-2"><span className={`px-1 py-0.5 border border-zinc-900 ${k.scope === "read_write" ? "bg-yellow-100" : "bg-blue-100"}`}>{k.scope}</span></td>
                <td className="p-2 text-right">{k.call_count || 0}</td>
                <td className="p-2 text-zinc-500">{k.last_used_at ? new Date(k.last_used_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) : "—"}</td>
                <td className="p-2">{k.revoked ? <span className="text-rose-700">REVOKED</span> : <span className="text-emerald-700">ACTIVE</span>}</td>
                <td className="p-2 text-right">
                  {!k.revoked && (
                    <button onClick={() => onRevoke(k.id)} className="text-rose-600 hover:text-rose-800" data-testid={`revoke-key-${k.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showNew && <NewKeyModal onClose={() => setShowNew(false)} onCreate={onCreate} />}
    </div>
  );
}

function NewKeyModal({ onClose, onCreate }) {
  const [label, setLabel] = useState("");
  const [scope, setScope] = useState("read_only");
  return (
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-3" onClick={onClose}>
      <div className="brut-card max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-lg font-black mb-3">New API Key</h3>
        <label className="label-mono">LABEL</label>
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Surveydrafts.us sync" className="brut-input w-full px-2 py-1.5 text-sm font-mono mb-3" data-testid="new-key-label" />
        <label className="label-mono">SCOPE</label>
        <select value={scope} onChange={(e) => setScope(e.target.value)} className="brut-input w-full px-2 py-1.5 text-sm font-mono mb-4" data-testid="new-key-scope">
          <option value="read_only">read_only — GET only (clients, vendors, teams, jobs, invoices)</option>
          <option value="read_write">read_write — also includes future POST/PATCH endpoints</option>
        </select>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="brut-btn-secondary px-3 py-1.5 text-xs">CANCEL</button>
          <button onClick={() => onCreate(label, scope)} className="brut-btn px-3 py-1.5 text-xs bg-emerald-300 hover:bg-emerald-400" data-testid="confirm-new-key">CREATE</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function WebhooksPanel() {
  const [whs, setWhs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [justSecret, setJustSecret] = useState(null);
  const [logFor, setLogFor] = useState(null);

  const load = async () => {
    let data = [];
    try {
      const resp = await api.get("/integrations/ld-webhooks");
      data = resp.data || [];
    } catch { /* keep [] */ }
    setWhs(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const [creating, setCreating] = useState(false);
  const onCreate = async (label, url, events) => {
    setCreating(true);
    try {
      const resp = await api.post("/integrations/ld-webhooks", { label, url, events });
      setJustSecret(resp.data);
      setShowNew(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setCreating(false);
  };

  const onTest = async (id) => {
    try {
      const { data } = await api.post(`/integrations/ld-webhooks/${id}/test`);
      toast[data.ok ? "success" : "error"](data.ok ? "Webhook delivered ✓" : "Delivery failed — check the log");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const onDelete = async (id) => {
    if (!confirm("Delete this webhook? Existing deliveries log will also be cleared.")) return;
    try { await api.delete(`/integrations/ld-webhooks/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="brut-card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Webhook className="w-4 h-4" />
          <h2 className="font-heading text-xl font-black">Webhooks</h2>
          <span className="label-mono text-zinc-500">{whs.length}</span>
        </div>
        <button onClick={() => setShowNew(true)} className="brut-btn px-3 py-1.5 text-xs bg-emerald-300 hover:bg-emerald-400 inline-flex items-center gap-1" data-testid="create-webhook-btn">
          <Plus className="w-3 h-3" /> NEW WEBHOOK
        </button>
      </div>

      {justSecret && (
        <div className="border-2 border-emerald-700 bg-emerald-50 p-3 mb-4" data-testid="just-secret">
          <div className="label-mono text-emerald-800">✓ WEBHOOK CREATED — COPY THE SIGNING SECRET NOW</div>
          <div className="text-[10px] text-zinc-600 mt-1">Use this secret on your receiver to verify <code>X-LD-Signature: sha256=&lt;HMAC&gt;</code></div>
          <div className="flex gap-2 mt-2 items-center">
            <code className="font-mono text-sm font-bold flex-1 truncate select-all">{justSecret.secret}</code>
            <button onClick={() => { navigator.clipboard.writeText(justSecret.secret); toast.success("Copied"); }} className="brut-btn px-2 py-1 text-xs">COPY</button>
            <button onClick={() => setJustSecret(null)} className="brut-btn-secondary px-2 py-1 text-xs">DISMISS</button>
          </div>
        </div>
      )}

      {loading && <div className="text-xs font-mono text-zinc-500">Loading…</div>}
      {!loading && whs.length === 0 && (
        <div className="text-xs font-mono text-zinc-500 py-6 text-center">No webhooks yet. Add a receiver URL to start streaming events.</div>
      )}
      {whs.length > 0 && (
        <table className="w-full text-xs font-mono" data-testid="webhooks-table">
          <thead className="border-b-2 border-zinc-900 bg-zinc-50">
            <tr><th className="text-left p-2">LABEL</th><th className="text-left p-2">URL</th><th className="text-left p-2">EVENTS</th><th className="text-right p-2">DELIVERED</th><th className="text-right p-2">FAILED</th><th></th></tr>
          </thead>
          <tbody>
            {whs.map((w) => (
              <tr key={w.id} className="border-b border-zinc-100" data-testid={`wh-row-${w.id}`}>
                <td className="p-2">{w.label}</td>
                <td className="p-2 max-w-[260px] truncate" title={w.url}>{w.url}</td>
                <td className="p-2">{(w.events || []).join(", ")}</td>
                <td className="p-2 text-right">{w.delivery_count || 0}</td>
                <td className={`p-2 text-right ${(w.failure_count || 0) > 0 ? "text-rose-600 font-bold" : ""}`}>{w.failure_count || 0}</td>
                <td className="p-2 text-right">
                  <button onClick={() => onTest(w.id)} className="text-emerald-700 hover:text-emerald-900 mr-2" title="Send test ping" data-testid={`wh-test-${w.id}`}><Send className="w-3.5 h-3.5" /></button>
                  <button onClick={() => setLogFor(w)} className="text-zinc-700 hover:text-zinc-900 mr-2" title="View delivery log" data-testid={`wh-log-${w.id}`}><RefreshCw className="w-3.5 h-3.5" /></button>
                  <button onClick={() => onDelete(w.id)} className="text-rose-600 hover:text-rose-800" data-testid={`wh-del-${w.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showNew && <NewWebhookModal onClose={() => setShowNew(false)} onCreate={onCreate} />}
      {logFor && <DeliveryLogModal webhook={logFor} onClose={() => setLogFor(null)} />}
    </div>
  );
}

function NewWebhookModal({ onClose, onCreate }) {
  const ALL_EVENTS = ["job.created", "job.stage_changed", "invoice.created", "invoice.paid", "client.created", "client.updated"];
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState(["*"]);
  const toggleEvent = (e) => {
    if (e === "*") return setEvents(["*"]);
    const current = events.filter((x) => x !== "*");
    if (current.includes(e)) setEvents(current.filter((x) => x !== e));
    else setEvents([...current, e]);
  };
  return (
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-3" onClick={onClose}>
      <div className="brut-card max-w-lg w-full p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-lg font-black mb-3">New Webhook</h3>
        <label className="label-mono">LABEL</label>
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Surveydrafts.us receiver" className="brut-input w-full px-2 py-1.5 text-sm font-mono mb-3" data-testid="new-wh-label" />
        <label className="label-mono">RECEIVER URL</label>
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://surveydrafts.us/api/webhooks/landdrafters" className="brut-input w-full px-2 py-1.5 text-sm font-mono mb-3" data-testid="new-wh-url" />
        <label className="label-mono mb-1 block">EVENTS</label>
        <div className="flex flex-wrap gap-1 mb-4">
          {["*", ...ALL_EVENTS].map((e) => (
            <button key={e} onClick={() => toggleEvent(e)} className={`px-2 py-1 border border-zinc-900 text-xs font-mono ${events.includes(e) ? "bg-yellow-200" : "bg-white"}`} data-testid={`wh-event-${e}`}>{e === "*" ? "ALL EVENTS" : e}</button>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="brut-btn-secondary px-3 py-1.5 text-xs">CANCEL</button>
          <button onClick={() => onCreate(label, url, events)} disabled={!url} className="brut-btn px-3 py-1.5 text-xs bg-emerald-300 hover:bg-emerald-400 disabled:opacity-50" data-testid="confirm-new-wh">CREATE</button>
        </div>
      </div>
    </div>
  );
}

function DeliveryLogModal({ webhook, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/integrations/ld-webhooks/${webhook.id}/deliveries`, { params: { limit: 50 } })
      .then(({ data }) => setRows(data || []))
      .catch(() => setRows([]));
  }, [webhook.id]);
  return (
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-3" onClick={onClose}>
      <div className="brut-card max-w-3xl w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b-2 border-zinc-900 bg-white">
          <div className="label-mono">DELIVERY LOG</div>
          <h3 className="font-heading text-lg font-black">{webhook.label}</h3>
          <div className="text-[10px] font-mono text-zinc-500 truncate">{webhook.url}</div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {rows === null && <div className="p-6 text-xs font-mono text-zinc-500">Loading…</div>}
          {rows?.length === 0 && <div className="p-6 text-xs font-mono text-zinc-500 text-center">No deliveries yet.</div>}
          {rows?.length > 0 && (
            <table className="w-full text-xs font-mono">
              <thead className="bg-zinc-50 border-b border-zinc-300 sticky top-0"><tr><th className="text-left p-2">EVENT</th><th className="text-right p-2">STATUS</th><th className="text-left p-2">AT</th><th className="text-left p-2">ERROR</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-zinc-100">
                    <td className="p-2">{r.event}</td>
                    <td className="p-2 text-right">{r.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700 inline" /> : <XCircle className="w-3.5 h-3.5 text-rose-700 inline" />} {r.status || "—"}</td>
                    <td className="p-2 text-zinc-500">{new Date(r.at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}</td>
                    <td className="p-2 text-zinc-600 max-w-[200px] truncate" title={r.error || ""}>{r.error || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="p-3 border-t border-zinc-300 bg-zinc-50 text-right">
          <button onClick={onClose} className="brut-btn-secondary px-3 py-1.5 text-xs">CLOSE</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// INBOUND from Surveydrafts.us — admin shares URL + secret with SD so SD can
// push real-time events into Land Drafters (instead of LD polling on a timer).
function InboundFromSurveydraftsPanel() {
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const receiverUrl = `${apiBase}/api/sd-inbound/webhook`;
  const [cfg, setCfg] = useState(null);
  const [secret, setSecret] = useState(null);   // only set right after rotate
  const [showSecret, setShowSecret] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [deliveries, setDeliveries] = useState(null);

  const loadCfg = async () => {
    try {
      const { data } = await api.get("/sd-inbound/config");
      setCfg(data);
    } catch {
      setCfg({ configured: false, supported_events: [] });
    }
  };
  useEffect(() => { loadCfg(); }, []);

  const onRotate = async () => {
    if (cfg?.configured && !confirm(
      "Rotating will INVALIDATE the existing secret. Surveydrafts.us will start failing until you update its webhook with the new secret. Continue?"
    )) return;
    setRotating(true);
    try {
      const { data } = await api.post("/sd-inbound/rotate-secret");
      setSecret(data.secret);
      setShowSecret(true);
      await loadCfg();
      toast.success("New signing secret generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to rotate");
    }
    setRotating(false);
  };

  const onDisable = async () => {
    if (!confirm("Disable inbound webhook? Surveydrafts.us will receive 503 until you rotate a new secret.")) return;
    try {
      await api.delete("/sd-inbound/secret");
      setSecret(null);
      await loadCfg();
      toast.success("Inbound webhook disabled");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openLog = async () => {
    setShowLog(true);
    setDeliveries(null);
    try {
      const { data } = await api.get("/sd-inbound/deliveries", { params: { limit: 100 } });
      setDeliveries(data || []);
    } catch { setDeliveries([]); }
  };

  return (
    <div className="brut-card p-5 bg-blue-50/40">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ArrowDownToLine className="w-4 h-4" />
          <h2 className="font-heading text-xl font-black">Inbound from Surveydrafts.us</h2>
          {cfg?.configured ? (
            <span className="label-mono text-emerald-700 border border-emerald-700 px-1.5 py-0.5">ACTIVE</span>
          ) : (
            <span className="label-mono text-zinc-500 border border-zinc-400 px-1.5 py-0.5">NOT CONFIGURED</span>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={openLog}
            className="brut-btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1"
            data-testid="sd-inbound-view-log"
          ><RefreshCw className="w-3 h-3" /> DELIVERIES</button>
          <button
            onClick={onRotate}
            disabled={rotating}
            className="brut-btn px-3 py-1.5 text-xs bg-emerald-300 hover:bg-emerald-400 inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="sd-inbound-rotate"
          ><RotateCw className="w-3 h-3" /> {cfg?.configured ? "ROTATE SECRET" : "GENERATE SECRET"}</button>
        </div>
      </div>

      <p className="text-xs font-mono text-zinc-700 mb-3">
        Let Surveydrafts.us PUSH real-time events INTO Land Drafters (instead of LD polling). Paste the URL and secret below into the Surveydrafts admin → Webhooks page.
      </p>

      {/* Receiver URL */}
      <div className="brut-card bg-white p-3 mb-3">
        <div className="label-mono text-zinc-500">SURVEYDRAFTS.US WEBHOOK URL (paste in SD)</div>
        <div className="flex items-center gap-2 mt-1">
          <code className="font-mono text-sm font-bold flex-1 truncate" title={receiverUrl} data-testid="sd-inbound-url">{receiverUrl}</code>
          <button
            onClick={() => { navigator.clipboard.writeText(receiverUrl); toast.success("URL copied"); }}
            className="brut-btn-secondary px-2 py-1 text-[10px] inline-flex items-center gap-1"
            data-testid="copy-sd-inbound-url"
          ><Copy className="w-3 h-3" /> COPY</button>
        </div>
      </div>

      {/* Secret reveal — only when just rotated */}
      {secret && (
        <div className="border-2 border-emerald-700 bg-emerald-50 p-3 mb-3" data-testid="sd-inbound-secret-reveal">
          <div className="label-mono text-emerald-800">✓ NEW SIGNING SECRET — COPY NOW (not retrievable again)</div>
          <div className="text-[10px] text-zinc-600 mt-1">
            Surveydrafts.us must HMAC-SHA256 the request body with this secret and send it in header <code>X-LD-Inbound-Signature: sha256=&lt;hex&gt;</code>.
          </div>
          <div className="flex gap-2 mt-2 items-center">
            <code className="font-mono text-sm font-bold flex-1 truncate select-all">
              {showSecret ? secret : "•".repeat(Math.min(secret.length, 48))}
            </code>
            <button onClick={() => setShowSecret((s) => !s)} className="brut-btn-secondary px-2 py-1 text-xs">
              {showSecret ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            </button>
            <button onClick={() => { navigator.clipboard.writeText(secret); toast.success("Secret copied"); }} className="brut-btn px-2 py-1 text-xs">COPY</button>
            <button onClick={() => { setSecret(null); setShowSecret(false); }} className="brut-btn-secondary px-2 py-1 text-xs">DISMISS</button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <Stat label="DELIVERIES" value={cfg?.delivery_count || 0} />
        <Stat label="SUCCESS" value={cfg?.success_count || 0} className="text-emerald-700" />
        <Stat label="FAILED" value={cfg?.failure_count || 0} className={`${(cfg?.failure_count || 0) > 0 ? "text-rose-700 font-bold" : ""}`} />
        <Stat label="LAST DELIVERY" value={cfg?.last_delivery_at ? new Date(cfg.last_delivery_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) : "—"} small />
      </div>

      {/* Setup steps */}
      <details className="brut-card bg-white p-3" open>
        <summary className="cursor-pointer label-mono">SETUP STEPS (one-time)</summary>
        <ol className="list-decimal pl-5 text-xs font-mono text-zinc-700 mt-2 space-y-1.5">
          <li>Click <b>{cfg?.configured ? "ROTATE SECRET" : "GENERATE SECRET"}</b> above. Copy the secret immediately.</li>
          <li>Open <code>surveydrafts.us</code> → <b>Settings → Webhooks</b> (or <b>Integrations</b>) → <b>Add Subscriber</b>.</li>
          <li>Paste the receiver URL: <code className="bg-zinc-100 px-1">{receiverUrl}</code></li>
          <li>Paste the signing secret you just copied.</li>
          <li>Subscribe to events: <code>job.created</code>, <code>job.updated</code>, <code>job.stage_changed</code>, <code>vendor.*</code>, <code>team_member.*</code>, <code>client.*</code>.</li>
          <li>Save. SD will start posting; verify delivery in the <b>DELIVERIES</b> log.</li>
        </ol>
        <div className="text-[10px] font-mono text-zinc-500 mt-2">
          Signature header accepted (in order): <code>X-LD-Inbound-Signature</code>, <code>X-Signature</code>, <code>X-SD-Signature</code>. Format: <code>sha256=&lt;hex&gt;</code> or bare hex.
        </div>
      </details>

      {/* Supported events */}
      <div className="mt-3">
        <div className="label-mono text-zinc-500 mb-1">SUPPORTED EVENTS</div>
        <div className="flex flex-wrap gap-1">
          {(cfg?.supported_events || []).map((e) => (
            <code key={e} className="text-[10px] bg-white border border-zinc-300 px-1.5 py-0.5">{e}</code>
          ))}
        </div>
      </div>

      {cfg?.configured && (
        <div className="text-right mt-3">
          <button onClick={onDisable} className="text-xs font-mono text-rose-600 hover:text-rose-800 underline" data-testid="sd-inbound-disable">
            Disable inbound webhook
          </button>
        </div>
      )}

      <SdSubscribersCard receiverUrl={receiverUrl} />

      {showLog && <InboundLogModal rows={deliveries} onClose={() => setShowLog(false)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SD-side subscribers — auto-configure SD's webhook UI from here in one click.
function SdSubscribersCard({ receiverUrl }) {
  const [subs, setSubs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [configuring, setConfiguring] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sd-inbound/sd-subscribers");
      setSubs(data || []);
    } catch (e) {
      const msg = e.response?.data?.detail || "Could not reach Surveydrafts.us";
      setSubs({ error: msg });
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onAutoConfigure = async () => {
    const url = prompt(
      "Receiver URL Surveydrafts.us will POST to.\n\nUse https://landdrafters.in for production, or your preview origin for testing.",
      "https://landdrafters.in",
    );
    if (!url) return;
    if (!confirm(
      `Auto-configure Surveydrafts.us to push events to:\n${url}/api/sd-inbound/webhook\n\n• Existing LD-pointing subscribers on SD will be DELETED.\n• SD will assign a fresh signing secret — we'll save it on our side automatically.\n\nContinue?`
    )) return;
    setConfiguring(true);
    try {
      const { data } = await api.post("/sd-inbound/sd-subscribers/auto-configure", {
        receiver_url: url,
        label: "Land Drafters Inbound",
        delete_stale: true,
      });
      const removed = (data.removed_stale || []).length;
      const secretMsg = data.secret_replaced_by_sd ? " · LD secret auto-synced." : "";
      toast.success(`Configured. ${removed} stale removed.${secretMsg}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-configure failed");
    }
    setConfiguring(false);
  };

  const onDelete = async (id) => {
    if (!confirm("Delete this subscriber on Surveydrafts.us?")) return;
    try {
      await api.delete(`/sd-inbound/sd-subscribers/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const hasError = subs && !Array.isArray(subs) && subs.error;
  const rows = Array.isArray(subs) ? subs : [];
  const ours = rows.filter((r) => (r.url || "").includes("/sd-inbound/webhook"));
  const stale = rows.filter((r) =>
    !((r.url || "").includes("/sd-inbound/webhook")) &&
    ((r.label || "").toLowerCase().includes("landdrafters") || (r.url || "").toLowerCase().includes("landdrafters"))
  );

  return (
    <div className="brut-card bg-white p-3 mt-3" data-testid="sd-subscribers-card">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <ExternalLink className="w-3.5 h-3.5" />
          <h3 className="font-heading text-sm font-black">Surveydrafts.us → subscribers (the OTHER side)</h3>
          {ours.length > 0 && <span className="label-mono text-emerald-700 border border-emerald-700 px-1.5 py-0.5">{ours.length} ACTIVE</span>}
          {stale.length > 0 && <span className="label-mono text-rose-700 border border-rose-700 px-1.5 py-0.5">{stale.length} STALE</span>}
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={loading} className="brut-btn-secondary px-2 py-1 text-[10px] inline-flex items-center gap-1" data-testid="sd-subs-refresh">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> {loading ? "…" : "REFRESH"}
          </button>
          <button onClick={onAutoConfigure} disabled={configuring} className="brut-btn px-3 py-1 text-[11px] bg-emerald-300 hover:bg-emerald-400 inline-flex items-center gap-1 disabled:opacity-50" data-testid="sd-subs-auto-configure">
            <RotateCw className={`w-3 h-3 ${configuring ? "animate-spin" : ""}`} /> {configuring ? "CONFIGURING…" : "AUTO-CONFIGURE"}
          </button>
        </div>
      </div>

      <p className="text-[10px] font-mono text-zinc-600 mb-2">
        These are the webhook subscribers configured ON Surveydrafts.us that point back to Land Drafters. Click <b>AUTO-CONFIGURE</b> to register a fresh one with the correct URL + signing secret in one shot.
      </p>

      {hasError && (
        <div className="text-xs font-mono text-rose-700 py-2">⚠ {subs.error}</div>
      )}

      {!hasError && rows.length === 0 && !loading && (
        <div className="text-xs font-mono text-zinc-500 py-3 text-center">No subscribers on Surveydrafts.us yet. Click AUTO-CONFIGURE to register one.</div>
      )}

      {rows.length > 0 && (
        <table className="w-full text-[11px] font-mono" data-testid="sd-subs-table">
          <thead className="border-b border-zinc-300 bg-zinc-50">
            <tr>
              <th className="text-left p-1.5">LABEL</th>
              <th className="text-left p-1.5">URL</th>
              <th className="text-right p-1.5">FAILS</th>
              <th className="text-left p-1.5">LAST FIRED</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isOurs = (r.url || "").includes("/sd-inbound/webhook");
              const isStale = !isOurs && ((r.label || "").toLowerCase().includes("landdrafters") || (r.url || "").toLowerCase().includes("landdrafters"));
              return (
                <tr key={r.id} className={`border-b border-zinc-100 ${isOurs ? "bg-emerald-50" : isStale ? "bg-rose-50" : ""}`} data-testid={`sd-sub-row-${r.id}`}>
                  <td className="p-1.5">
                    {r.label}
                    {isOurs && <span className="ml-1.5 label-mono text-emerald-700">OURS</span>}
                    {isStale && <span className="ml-1.5 label-mono text-rose-700">STALE</span>}
                  </td>
                  <td className="p-1.5 max-w-[280px] truncate text-zinc-600" title={r.url}>{r.url}</td>
                  <td className={`p-1.5 text-right ${(r.failure_count || 0) > 0 ? "text-rose-700 font-bold" : ""}`}>{r.failure_count || 0}</td>
                  <td className="p-1.5 text-zinc-500">{r.last_triggered_at ? new Date(r.last_triggered_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) : "—"}</td>
                  <td className="p-1.5 text-right">
                    <button onClick={() => onDelete(r.id)} className="text-rose-600 hover:text-rose-800" title="Delete on SD" data-testid={`sd-sub-del-${r.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div className="text-[10px] font-mono text-zinc-500 mt-2">
        Receiver URL we register: <code className="bg-zinc-100 px-1">{receiverUrl}</code>
      </div>
    </div>
  );
}

function Stat({ label, value, className = "", small = false }) {
  return (
    <div className="brut-card bg-white p-2">
      <div className="label-mono text-zinc-500">{label}</div>
      <div className={`font-heading font-black mt-0.5 ${small ? "text-xs" : "text-2xl"} ${className}`}>{value}</div>
    </div>
  );
}

function InboundLogModal({ rows, onClose }) {
  return (
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-3" onClick={onClose}>
      <div className="brut-card max-w-3xl w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b-2 border-zinc-900 bg-white">
          <div className="label-mono">INBOUND DELIVERY LOG</div>
          <h3 className="font-heading text-lg font-black">From Surveydrafts.us → Land Drafters</h3>
        </div>
        <div className="flex-1 overflow-y-auto">
          {rows === null && <div className="p-6 text-xs font-mono text-zinc-500">Loading…</div>}
          {rows?.length === 0 && <div className="p-6 text-xs font-mono text-zinc-500 text-center">No deliveries yet. Configure Surveydrafts.us to POST here and they&apos;ll show up.</div>}
          {rows?.length > 0 && (
            <table className="w-full text-xs font-mono">
              <thead className="bg-zinc-50 border-b border-zinc-300 sticky top-0">
                <tr><th className="text-left p-2">EVENT</th><th className="text-right p-2">STATUS</th><th className="text-left p-2">AT</th><th className="text-left p-2">DETAILS</th></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-zinc-100 align-top" data-testid={`sd-inbound-row-${r.id}`}>
                    <td className="p-2 whitespace-nowrap">{r.event}</td>
                    <td className="p-2 text-right whitespace-nowrap">
                      {r.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700 inline" /> : <XCircle className="w-3.5 h-3.5 text-rose-700 inline" />} {r.status || "—"}
                    </td>
                    <td className="p-2 text-zinc-500 whitespace-nowrap">{new Date(r.at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}</td>
                    <td className="p-2 text-zinc-700">
                      {r.error ? <span className="text-rose-700">{r.error}</span> : (
                        <code className="text-[10px] text-zinc-600">{JSON.stringify(r.summary || {})}</code>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="p-3 border-t border-zinc-300 bg-zinc-50 text-right">
          <button onClick={onClose} className="brut-btn-secondary px-3 py-1.5 text-xs">CLOSE</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function Docs() {
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const eps = [
    { method: "GET",  path: "/api/external/whoami",   desc: "Sanity check + key metadata" },
    { method: "GET",  path: "/api/external/clients",  desc: "?limit=&offset=" },
    { method: "GET",  path: "/api/external/vendors",  desc: "" },
    { method: "GET",  path: "/api/external/teams",    desc: "?vendor_id=&limit=" },
    { method: "GET",  path: "/api/external/jobs",     desc: "?limit=&offset=&since=&stage=&client_id=" },
    { method: "GET",  path: "/api/external/jobs/{id}",   desc: "Includes cached SD timeline" },
    { method: "GET",  path: "/api/external/invoices", desc: "?limit=&offset=&status=&client_id=" },
    { method: "GET",  path: "/api/external/invoices/{id}", desc: "" },
  ];
  return (
    <div className="brut-card p-5">
      <h2 className="font-heading text-xl font-black flex items-center gap-2 mb-3">
        <Globe className="w-4 h-4" /> Quick Reference
      </h2>
      <div className="text-xs font-mono text-zinc-700 mb-3">
        Auth header for every external call: <code className="bg-zinc-100 px-1">X-LD-API-Key: ld_…</code>
      </div>
      <table className="w-full text-xs font-mono mb-4">
        <thead className="bg-zinc-50 border-b border-zinc-300"><tr><th className="text-left p-2">METHOD</th><th className="text-left p-2">PATH</th><th className="text-left p-2">PARAMS</th></tr></thead>
        <tbody>
          {eps.map((e, i) => (
            <tr key={i} className="border-b border-zinc-100">
              <td className="p-2 text-emerald-700 font-bold">{e.method}</td>
              <td className="p-2"><code className="bg-zinc-100 px-1">{e.path}</code></td>
              <td className="p-2 text-zinc-600">{e.desc}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 className="font-heading text-base font-black mb-2">Webhook payload format</h3>
      <pre className="bg-zinc-50 border border-zinc-300 p-2 text-[10px] overflow-x-auto">{`POST <your_receiver>
Content-Type: application/json
User-Agent: LandDrafters-Webhook/1.0
X-LD-Event: job.stage_changed
X-LD-Delivery: 5a7b...
X-LD-Signature: sha256=<HMAC of body with your webhook secret>

{
  "event": "job.stage_changed",
  "data": {
    "job_id": "job_001e3babfbd6",
    "job_number": "26060305",
    "from_stage": "drafter",
    "to_stage": "qa1",
    "user": "Land Drafters Admin",
    "source": "manual"
  },
  "ts": "2026-06-08T09:57:52Z"
}`}</pre>
      <div className="text-[10px] font-mono text-zinc-500 mt-2">
        Verify the signature with HMAC-SHA256 using the secret shown when you created the webhook. Reject any payload whose signature doesn&apos;t match.
      </div>
    </div>
  );
}
